import { t } from '../i18n.js';
import { HUB_PATH, primaryCatalogItems } from './catalog-items.js';

// Inactive tabs override the icon custom properties to a single muted tone
// (matching `text-ink-muted`) so only the active tab reads with the brand
// teal/green accent; the active tab simply inherits the default `--bi-*`
// values cascaded from `:root`/`.dark` in app.css — no override needed.
// `--bi-accent` is deliberately absent: the vendored duotone glyphs are
// two-tone, so overriding a third colour would have no effect.
const MUTED_ICON_VARS = {
  '--bi-stroke': '#7c948f',
  '--bi-fill': 'transparent',
};


/**
 * Fixed bottom tab bar for phones and small tablets (< `lg` breakpoint).
 * Hidden on desktop via `lg:hidden`, where the sidebar carries the same
 * structure at a different density.
 *
 * The tab set is projected from the shared registry (`primary: true`) rather
 * than declared here. A module-local list is exactly how the mobile and
 * desktop navigations were able to drift into two different information
 * architectures: a registry addition reached one surface and missed the other.
 *
 * Tabs are real `<a href="#/...">` so browser back and screen-reader nav work
 * without extra wiring. The active tab is derived from `window.location.hash`
 * and refreshes on every `hashchange`.
 *
 * @param {{ currentPath?: string }} props
 * @returns {{ el: HTMLElement, refresh: (path?: string) => void, destroy: () => void }}
 */
export function bottomNav({ currentPath } = {}) {
  const tabs = primaryCatalogItems();

  const root = document.createElement('nav');
  root.setAttribute('aria-label', t('nav.primary_aria'));
  root.dataset.role = 'bottom-nav';
  root.className = [
    'lg:hidden fixed bottom-0 inset-x-0 z-40',
    // Fixed chrome (not a scrolling list card) — glass-panel carries the dark
    // "Depth & glass" blur treatment (D2); only the top edge needs a visible
    // hairline, so the other three sides of glass-panel's border are zeroed.
    'glass-panel border-x-0 border-b-0',
    // Safe-area inset (iPhone home-indicator zone). Tailwind arbitrary-value
    // syntax compiles to: padding-bottom: env(safe-area-inset-bottom);
    'pb-[env(safe-area-inset-bottom)]',
  ].join(' ');

  const inner = document.createElement('div');
  inner.className = 'flex';
  root.append(inner);

  /** @type {Array<{ path: string, el: HTMLAnchorElement }>} */
  const tabEls = [];

  function activeStyle(el, active) {
    if (active) {
      el.classList.add('text-primary-600', 'dark:text-primary-500', 'border-primary-600', 'dark:border-primary-500');
      el.classList.remove('text-ink-muted', 'border-transparent');
      for (const prop of Object.keys(MUTED_ICON_VARS)) el.style.removeProperty(prop);
    } else {
      el.removeAttribute('aria-current');
      el.classList.remove('text-primary-600', 'dark:text-primary-500', 'border-primary-600', 'dark:border-primary-500');
      el.classList.add('text-ink-muted', 'border-transparent');
      for (const [prop, value] of Object.entries(MUTED_ICON_VARS)) el.style.setProperty(prop, value);
    }
  }

  function pathFromHash(h) {
    if (!h) return '/';
    const stripped = h.startsWith('#') ? h.slice(1) : h;
    return stripped || '/';
  }

  let activePath = currentPath || pathFromHash(typeof window !== 'undefined' ? window.location.hash : '');

  for (const tab of tabs) {
    const a = document.createElement('a');
    a.href = '#' + tab.path;
    a.dataset.path = tab.path;
    a.className = [
      'flex-1 min-h-[56px]',
      'flex flex-col items-center justify-center gap-0.5',
      'text-xs',
      'border-t-2',
      'focus:outline-none focus:ring-2 focus:ring-primary',
    ].join(' ');

    const labelEl = document.createElement('span');
    labelEl.className = 'truncate max-w-full px-1';
    labelEl.textContent = t(tab.titleKey);

    a.append(tab.iconFn(), labelEl);
    inner.append(a);
    tabEls.push({ path: tab.path, el: a });
  }

  /**
   * Exact-path comparison alone left 17 of 21 routes with nothing lit. A tab is
   * current when the route equals its path or is a sub-route of it; when the
   * route belongs to none of the other three tabs, the hub tab carries
   * `aria-current="true"` — section-level rather than page-level — so some tab
   * is always lit.
   */
  function refresh(path) {
    activePath = path != null ? path : pathFromHash(window.location.hash);

    let pageMatch = null;
    for (const { path: tabPath, el } of tabEls) {
      const isPage = tabPath === activePath
        || (tabPath !== '/' && activePath.startsWith(tabPath + '/'));
      if (isPage) pageMatch = el;
      activeStyle(el, isPage);
      if (isPage) el.setAttribute('aria-current', 'page');
    }

    if (!pageMatch) {
      const hub = tabEls.find((tab) => tab.path === HUB_PATH);
      if (hub) {
        activeStyle(hub.el, true);
        hub.el.setAttribute('aria-current', 'true');
      }
    }
  }

  refresh(activePath);

  const onHashChange = () => refresh();
  if (typeof window !== 'undefined') {
    window.addEventListener('hashchange', onHashChange);
  }

  function destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('hashchange', onHashChange);
    }
  }

  return { el: root, refresh, destroy };
}
